import React, { createContext, useContext, useReducer, useMemo, Dispatch } from 'react';

// Define Voice state types
interface Voice {
  tone: string[];
  formality: string;
  avoidWords: string;
}

// Define User state types
export interface User {
  id: string;
  asfId: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  websiteId: string;
  voice: Voice;
  brandVoice: string;
  createdAt: Date;
}

// Define User state
interface UserState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Define action types
type UserAction =
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING' }
  | { type: 'SET_ERROR'; payload: string | null };

// Initial State
const initialUserState: UserState = {
  user: null,
  loading: false,
  error: null,
};

// Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: true };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
};

// Create Context interface
interface UserContextType {
  state: UserState;
  dispatch: Dispatch<UserAction>;
}

// Create Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider Component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialUserState);

  // Memoized value to avoid unnecessary re-renders
  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Hook for using UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
